'use client'

import { createContext, useContext } from 'react'
import { Tabs as TabsPrimitive } from '@base-ui/react/tabs'

import { cn } from '@/shared/lib/cn'

type TabsVariant = 'line' | 'attached'

const TabsVariantContext = createContext<TabsVariant>('line')

function Tabs({ className, variant = 'line', ...props }: TabsPrimitive.Root.Props & { variant?: TabsVariant }) {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.Root
        data-slot="tabs"
        data-variant={variant}
        className={cn('flex flex-col', variant === 'attached' ? 'isolate gap-0' : 'gap-2', className)}
        {...props}
      />
    </TabsVariantContext.Provider>
  )
}

function TabsList({ className, ...props }: TabsPrimitive.List.Props) {
  const variant = useContext(TabsVariantContext)
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        variant === 'attached'
          ? 'grid w-full auto-cols-fr grid-flow-col items-stretch gap-2'
          : 'text-muted-foreground inline-flex h-9 w-fit items-end justify-start gap-5 border-b bg-transparent',
        className
      )}
      {...props}
    />
  )
}

function TabsTrigger({ className, ...props }: TabsPrimitive.Tab.Props) {
  const variant = useContext(TabsVariantContext)
  // В attached нижний край вкладки перекрывает 2 px рамки панели, соединяя их в одну область.
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        'inline-flex flex-1 cursor-pointer gap-1.5 [font-size:var(--type-label-font-size)] [font-weight:var(--type-label-font-weight)] [line-height:var(--type-label-line-height)] transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/20 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
        variant === 'attached'
          ? 'relative -mb-0.5 min-w-0 flex-col items-start justify-start rounded-t-xl border-2 border-border border-b-primary bg-card px-4 py-3 text-left whitespace-normal text-foreground focus-visible:z-20 data-active:z-10 data-active:border-primary data-active:border-b-muted data-active:bg-muted'
          : 'text-muted-foreground -mb-px h-9 items-center justify-center border-b-2 border-transparent px-1 py-2 whitespace-nowrap data-active:border-primary data-active:text-primary',
        className
      )}
      {...props}
    />
  )
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  const variant = useContext(TabsVariantContext)
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn(
        'flex-1 outline-none',
        variant === 'attached' && 'rounded-b-xl border-2 border-primary bg-muted p-4 sm:p-5',
        className
      )}
      {...props}
    />
  )
}

export { Tabs, TabsContent, TabsList, TabsTrigger }
